import type {
  DateOperatorFilterInput,
  FilterConfig,
  FilterOption,
  LineItemEndDateFilterInput,
} from '../../../apis/graphql/types';
import { ComaprisonOperation, DateOperator, FilterOperator } from '../../../apis/graphql/types';
import type { OperatorFilterValue } from '../../../contexts/FilterContext/hooks/utils';
import {
  isOperatorFilterValue,
  isRelativeDateFilterFilled,
  translateLegacyBrandOperator,
} from '../../../contexts/FilterContext/hooks/utils';
import { isComparisonDateOperator } from './FilterRow/DateControl/types';
import type { MultiSelectState } from './FilterRow/FilterRow';
import { filterOptionsToValues } from './multiSelectFilterUtils';

// KATANA-1509: re-exported so callers of this module (FilterPanel.tsx, this file's own tests)
// don't also need to know these live in contexts/FilterContext/hooks/utils — that's the canonical
// home shared with the STRING_OPERATOR_FIELDS family (see the comment there for why).
export type { OperatorFilterValue };
export { isOperatorFilterValue, translateLegacyBrandOperator };

/**
 * Maps a UI FilterOperator to the ComaprisonOperation enum used by campaign-searcher's
 * RangeFilterInput.minOperator / maxOperator fields. Returns null for non-range operators
 * (IN, NOT_IN, BETWEEN, date operators, etc.).
 */
export function filterOperatorToComparisonOperation(op: FilterOperator): ComaprisonOperation | null {
  switch (op) {
    case FilterOperator.GreaterThan:
      return ComaprisonOperation.GreaterThan;
    case FilterOperator.LessThan:
      return ComaprisonOperation.LessThan;
    case FilterOperator.Equals:
      return ComaprisonOperation.Equal;
    case FilterOperator.NotEquals:
      return ComaprisonOperation.NotEqual;
    case FilterOperator.LessThanOrEqualTo:
      return ComaprisonOperation.LessThanOrEqual;
    case FilterOperator.GreaterThanOrEqualTo:
      return ComaprisonOperation.GreaterThanOrEqual;
    case FilterOperator.IsEmpty:
      return ComaprisonOperation.IsEmpty;
    case FilterOperator.IsNotEmpty:
      return ComaprisonOperation.IsNotEmpty;
    default:
      return null;
  }
}

/**
 * Returns true when a committed date filter has enough data to produce a valid filter
 * clause in campaign-searcher. Handles two variants:
 *
 * 1. Calendar-based (DateOperatorFilterInput / LineItemEndDateFilterInput with `operator`):
 *    - IsBetween: both startDate and endDate must be set
 *    - All others: date must be set
 *
 * 2. Range-based (LineItemEndDateFilterInput with `comparisonOperation` + `duration` + `unit`):
 *    - All three fields must be present and duration must be > 0
 *      (duration === Number.MIN_SAFE_INTEGER is the UI sentinel for "empty")
 *
 * 3. Relative (operator IS_RELATIVE_TO_TODAY): see isRelativeDateFilterFilled.
 */
export function isDateOperatorFilterFilled(
  filter: DateOperatorFilterInput | LineItemEndDateFilterInput | null | undefined
): boolean {
  if (!filter) return false;

  // Calendar-based: operator field is present and non-null
  if (filter.operator === DateOperator.IsBetween) {
    return !!(filter.startDate && filter.endDate);
  }
  // Relative or conditional boundary: direction is the discriminant.
  const dateFilter = filter as DateOperatorFilterInput;
  if (
    filter.operator === DateOperator.IsRelativeToToday ||
    (dateFilter.direction && filter.operator && isComparisonDateOperator(filter.operator))
  ) {
    return isRelativeDateFilterFilled(dateFilter);
  }
  if (filter.operator != null) {
    return !!filter.date;
  }

  // Range-based variant (LineItemEndDateFilterInput): operator is null/absent,
  // comparisonOperation + duration + unit carry the filter definition.
  const liFilter = filter as LineItemEndDateFilterInput;
  return !!(liFilter.comparisonOperation && liFilter.duration != null && liFilter.duration > 0 && liFilter.unit);
}

/**
 * Produces the GlobalFilters key-value mutations for a multi-select filter row that
 * supports an operator (Contains / Does Not Contain).
 *
 * KATANA-1509: when the config's own id differs from both includeKey and excludeKey (currently
 * only Brand — its catalog id moved to `brandIdOperator`, distinct from the legacy
 * brandIdList/excludeBrandIdList pair it used to share with its id), write the canonical
 * `{ operator, values }` object under `id` and null both legacy keys so a stale value from
 * before this migration (or a previous apply cycle) is never left in GlobalFilters.
 *
 * Otherwise (agency/industry-style configs, where id === includeKey), always writes both
 * includeKey and excludeKey so stale values from a previous apply cycle are never left behind:
 *
 * - Contains (CONTAINS):           { [includeKey]: values, [excludeKey]: null }
 * - Does Not Contain (DOES_NOT_CONTAIN): { [includeKey]: null,  [excludeKey]: values }
 *
 * Returns an empty object when config.includeExclude is absent (plain multi-select
 * without operator support — caller falls back to the standard single-key commit path).
 */
export function multiSelectToGlobalFilters(
  config: FilterConfig,
  state: MultiSelectState
): Record<string, FilterOption[] | string[] | OperatorFilterValue | null> {
  const { includeExclude, id } = config;
  if (!includeExclude) return {};

  const { includeKey, excludeKey } = includeExclude;
  const values: FilterOption[] | string[] = config.isSearchable ? state.options : filterOptionsToValues(state.options);

  if (id !== includeKey && id !== excludeKey) {
    return {
      [id]: { operator: state.operator, values } as OperatorFilterValue,
      [includeKey]: null,
      [excludeKey]: null,
    };
  }

  if (state.operator === FilterOperator.DoesNotContain) {
    return {
      [includeKey]: null,
      [excludeKey]: values,
    };
  }

  return {
    [includeKey]: values,
    [excludeKey]: null,
  };
}
